<template>
  <div class="dashboard-compras-page">
    <div class="dashboard-header">
      <h1>Panel de Trabajo - Compras</h1>
      <p class="subtitle">Gestión de propuestas pendientes de revisión</p>
    </div>

    <!-- Filtros -->
    <div class="filters-section">
      <el-card shadow="never">
        <div class="filters-grid">
          <div class="filter-item">
            <label>Barco:</label>
            <el-select
              v-model="filtros.buque"
              placeholder="Todos los barcos"
              filterable
              clearable
              style="width: 100%"
              @change="aplicarFiltros"
            >
              <el-option
                v-for="buque in listaBarcos"
                :key="buque"
                :label="buque"
                :value="buque"
              />
            </el-select>
          </div>
          <div class="filter-item">
            <label>Tipo de Propuesta:</label>
            <el-select
              v-model="filtros.tipo"
              placeholder="Todos los tipos"
              clearable
              style="width: 100%"
              @change="aplicarFiltros"
            >
              <el-option label="MAQUINAS" value="MAQUINAS" />
              <el-option label="PUENTE" value="PUENTE" />
            </el-select>
          </div>
          <div class="filter-item">
            <label>Fecha Desde:</label>
            <el-date-picker
              v-model="filtros.fechaDesde"
              type="date"
              placeholder="Fecha desde"
              format="dd/MM/yyyy"
              value-format="yyyy-MM-dd"
              style="width: 100%"
              @change="aplicarFiltros"
            />
          </div>
          <div class="filter-item">
            <label>Fecha Hasta:</label>
            <el-date-picker
              v-model="filtros.fechaHasta"
              type="date"
              placeholder="Fecha hasta"
              format="dd/MM/yyyy"
              value-format="yyyy-MM-dd"
              style="width: 100%"
              @change="aplicarFiltros"
            />
          </div>
          <div class="filter-actions">
            <el-button @click="limpiarFiltros">Limpiar Filtros</el-button>
          </div>
        </div>
      </el-card>
    </div>

    <!-- Resumen de propuestas -->
    <div class="resumen-section">
      <el-row :gutter="20">
        <el-col :span="6">
          <el-card class="resumen-card total">
            <div class="resumen-content">
              <div class="resumen-icon">
                <i class="el-icon-document"></i>
              </div>
              <div class="resumen-info">
                <div class="resumen-numero">{{ propuestasFiltradas.length }}</div>
                <div class="resumen-label">Total Propuestas</div>
              </div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="resumen-card pendientes">
            <div class="resumen-content">
              <div class="resumen-icon">
                <i class="el-icon-warning"></i>
              </div>
              <div class="resumen-info">
                <div class="resumen-numero">{{ totalLineasPendientes }}</div>
                <div class="resumen-label">Líneas pendientes de revisión</div>
              </div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="resumen-card maquinas">
            <div class="resumen-content">
              <div class="resumen-icon">
                <i class="el-icon-setting"></i>
              </div>
              <div class="resumen-info">
                <div class="resumen-numero">{{ propuestasMaquinas }}</div>
                <div class="resumen-label">Propuestas Maquinas</div>
              </div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="resumen-card puente">
            <div class="resumen-content">
              <div class="resumen-icon">
                <i class="el-icon-ship"></i>
              </div>
              <div class="resumen-info">
                <div class="resumen-numero">{{ propuestasPuente }}</div>
                <div class="resumen-label">Propuestas Puente</div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- Tabla de propuestas -->
    <div class="propuestas-section">
      <el-card shadow="never" v-loading="loadingPropuestas">
        <div slot="header" class="table-header-actions">
          <span>Propuestas Pendientes</span>
          <el-button size="small" @click="recargarPropuestas">
            <i class="el-icon-refresh"></i> Actualizar
          </el-button>
        </div>
        <el-table
          :data="propuestasFiltradas"
          style="width: 100%"
          :default-sort="{prop: 'fechaCierre', order: 'ascending'}"
          @row-click="abrirPropuesta"
          :row-class-name="getRowClassName"
          :fit="true"
        >
          <el-table-column prop="codigo" label="Código" min-width="100" sortable>
            <template slot-scope="scope">
              <span class="codigo-propuesta">{{ scope.row.codigo }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="buque" label="Barco" min-width="120" sortable>
            <template slot-scope="scope">
              <span>{{ scope.row.buque }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="tipo" label="Tipo" min-width="100" sortable>
            <template slot-scope="scope">
              <el-tag :type="scope.row.tipo === 'MAQUINAS' ? 'primary' : 'success'" size="small">
                {{ scope.row.tipo }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="fechaCierre" label="Fecha Cierre" min-width="110" sortable>
            <template slot-scope="scope">
              <span>{{ formatearFecha(scope.row.fechaCierre) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="totalLineas" label="Total Líneas" min-width="110" align="center">
            <template slot-scope="scope">
              <span>{{ scope.row.totalLineas || 0 }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="lineasPendientes" label="Líneas Pendientes" min-width="140" align="center">
            <template slot-scope="scope">
              <el-tag
                v-if="scope.row.lineasPendientes > 0"
                type="warning"
                size="small"
              >
                {{ scope.row.lineasPendientes }}
              </el-tag>
              <span v-else style="color: #909399;">0</span>
            </template>
          </el-table-column>
          <el-table-column prop="lineasConOferta" label="Líneas con oferta" min-width="140" align="center">
            <template slot-scope="scope">
              <el-tag
                v-if="scope.row.lineasConOferta > 0"
                type="success"
                size="small"
              >
                {{ scope.row.lineasConOferta }}
              </el-tag>
              <span v-else style="color: #909399;">0</span>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import { getUrl } from '@/helpers/api'

export default {
  name: 'DashboardCompras',
  data () {
    return {
      propuestas: [],
      loadingPropuestas: false,
      filtros: {
        buque: null,
        tipo: null,
        fechaDesde: null,
        fechaHasta: null
      }
    }
  },
  computed: {
    listaBarcos () {
      const barcos = new Set()
      this.propuestas.forEach(prop => {
        if (prop.buque) {
          barcos.add(prop.buque)
        }
      })
      return Array.from(barcos).sort()
    },
    propuestasFiltradas () {
      let filtradas = [...this.propuestas]

      if (this.filtros.buque) {
        filtradas = filtradas.filter(p => p.buque === this.filtros.buque)
      }

      if (this.filtros.tipo) {
        filtradas = filtradas.filter(p => p.tipo === this.filtros.tipo)
      }

      if (this.filtros.fechaDesde) {
        filtradas = filtradas.filter(p => {
          if (!p.fechaCierre) return false
          return moment(p.fechaCierre, 'DD/MM/YYYY').isSameOrAfter(moment(this.filtros.fechaDesde))
        })
      }

      if (this.filtros.fechaHasta) {
        filtradas = filtradas.filter(p => {
          if (!p.fechaCierre) return false
          return moment(p.fechaCierre, 'DD/MM/YYYY').isSameOrBefore(moment(this.filtros.fechaHasta))
        })
      }

      return filtradas
    },
    totalLineasPendientes () {
      return this.propuestasFiltradas.reduce((acc, p) => acc + (p.lineasPendientes || 0), 0)
    },
    propuestasMaquinas () {
      return this.propuestasFiltradas.filter(p => p.tipo === 'MAQUINAS').length
    },
    propuestasPuente () {
      return this.propuestasFiltradas.filter(p => p.tipo === 'PUENTE').length
    }
  },
  async created () {
    await this.cargarPropuestas()
  },
  methods: {
    async cargarPropuestas () {
      try {
        this.loadingPropuestas = true
        const endpoints = getUrl()
        if (!endpoints) {
          this.$message.error('Error de configuración')
          return
        }

        // El backend filtrará solo propuestas con estado = 3 (compras)
        const response = await axios.post(
          `${endpoints.backend}entidad/propuestas_pedido/buscador/listarPropuestasGestionar`,
          { estado: 3 }
        )

        let data = []
        if (Array.isArray(response.data)) {
          data = response.data
        } else if (response.data && Array.isArray(response.data.data)) {
          data = response.data.data
        }

        // Procesar las propuestas para incluir información adicional
        this.propuestas = data.map(prop => ({
          ...prop,
          fecha: prop.fecha || (prop.cabecera && prop.cabecera.fecha) || '',
          tipo: prop.tipo || (prop.cabecera && prop.cabecera.tipo) || 'MAQUINAS',
          buque: prop.buque || (prop.cabecera && prop.cabecera.buque) || '',
          numeroPropuesta: prop.numeroPropuesta || (prop.cabecera && prop.cabecera.numeroPropuesta) || prop.codigo || '',
          totalLineas: prop.totalLineas || 0,
          lineasPendientes: prop.lineasPendientes || prop.lineasSinProveedor || 0,
          lineasConOferta: prop.lineasConOferta || 0,
          estado: prop.estado || 3,
          fechaCierre: prop.fechaCierre || (prop.cabecera && prop.cabecera.fechaCierre) || ''
        }))
      } catch (error) {
        console.error('Error al cargar propuestas:', error)
        this.$message.error('Error al cargar las propuestas')
        this.propuestas = []
      } finally {
        this.loadingPropuestas = false
      }
    },
    async recargarPropuestas () {
      await this.cargarPropuestas()
      this.$message.success('Propuestas actualizadas')
    },
    aplicarFiltros () {
      // Los filtros se aplican automáticamente mediante computed
    },
    limpiarFiltros () {
      this.filtros = {
        buque: null,
        tipo: null,
        fechaDesde: null,
        fechaHasta: null
      }
    },
    abrirPropuesta (propuesta) {
      // Navegar a la página de gestión con el ID de la propuesta
      this.$router.push({
        name: 'gestionar_propuestas_compras',
        params: { id: propuesta.id }
      })
    },
    formatearFecha (fecha) {
      if (!fecha) return '-'
      // Intentar parsear la fecha en diferentes formatos
      const fechaMoment = moment(fecha, ['DD/MM/YYYY', 'YYYY-MM-DD', 'DD-MM-YYYY'], true)
      if (fechaMoment.isValid()) {
        return fechaMoment.format('DD/MM/YYYY')
      }
      return fecha
    },
    getEstadoLabel (estado) {
      const estados = {
        3: 'Pendiente',
        4: 'En Revisión'
      }
      return estados[estado] || 'Desconocido'
    },
    getEstadoTagType (estado) {
      const tipos = {
        3: 'warning',
        4: 'info'
      }
      return tipos[estado] || ''
    },
    getRowClassName ({ row }) {
      // Resaltar filas con líneas pendientes
      if (row.lineasPendientes > 0) {
        return 'row-pendiente'
      }
      return ''
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables.scss';

.dashboard-compras-page {
  padding: 20px;
  background-color: $--color-background;
  min-height: 100vh;
}

.dashboard-header {
  margin-bottom: 30px;

  h1 {
    margin: 0 0 10px 0;
    color: $--color-primary;
    font-size: 2rem;
  }

  .subtitle {
    margin: 0;
    color: #666;
    font-size: 1rem;
  }
}

.filters-section {
  margin-bottom: 30px;

  .filters-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    align-items: flex-end;

    .filter-item {
      label {
        display: block;
        margin-bottom: 8px;
        font-weight: 500;
        color: #333;
        font-size: 0.9rem;
      }
    }

    .filter-actions {
      display: flex;
      align-items: flex-end;
    }
  }
}

.resumen-section {
  margin-bottom: 30px;

  .resumen-card {
    .resumen-content {
      display: flex;
      align-items: center;
      gap: 15px;

      .resumen-icon {
        font-size: 2.5rem;
        opacity: 0.8;
      }

      .resumen-info {
        flex: 1;

        .resumen-numero {
          font-size: 2rem;
          font-weight: bold;
          color: #333;
          line-height: 1.2;
        }

        .resumen-label {
          font-size: 0.9rem;
          color: #666;
          margin-top: 5px;
        }
      }
    }

    &.total {
      .resumen-icon {
        color: $--color-primary;
      }
    }

    &.pendientes {
      .resumen-icon {
        color: #e6a23c;
      }
    }

    &.maquinas {
      .resumen-icon {
        color: #409eff;
      }
    }

    &.puente {
      .resumen-icon {
        color: #67c23a;
      }
    }
  }
}

.propuestas-section {
  .table-header-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: bold;
  }

  .codigo-propuesta {
    font-weight: bold;
    color: $--color-primary;
  }

  ::v-deep .el-table {
    .row-pendiente {
      background-color: rgba(230, 162, 60, 0.1);

      &:hover {
        background-color: rgba(230, 162, 60, 0.2) !important;
      }
    }

    .el-table__row {
      cursor: pointer;

      &:hover {
        background-color: rgba($--color-header, 0.05);
      }
    }

    // Asegurar que la tabla no se parta
    .el-table__body-wrapper {
      overflow-x: auto;
    }

    .el-table__header-wrapper {
      overflow-x: hidden;
    }
  }
}

@media (max-width: 768px) {
  .filters-grid {
    grid-template-columns: 1fr !important;
  }

  .resumen-section {
    ::v-deep .el-col {
      margin-bottom: 15px;
    }
  }
}
</style>
